
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { PeriodPickerComponent } from '@portal-shared/app/components/inputs/period-picker/period-picker.component';
import { ModalComponent } from '@portal-shared/app/components/modal/modal.component';
import { Period } from '@portal-shared/app/entities/period.model';
import { PortalLoggerService } from '@portal-shared/app/services/portal-logger.service';
import { PeriodService } from '@services/period.service';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';

@Component({
    selector: 'al-shift-modal',
    templateUrl: './shift-modal.component.html',
    styleUrls: ['./shift-modal.component.scss'],
    imports: [FormsModule, ModalComponent, PeriodPickerComponent, TranslateModule]
})
export class ShiftModalComponent extends BaseComponent implements OnInit {
  @Input() public sourcePeriod!: Period;
  public firstPlannablePeriod!: Period;
  public destinationPeriod?: Period;
  public shiftPeriodCount = 1;
  public isShiftInvalid = false; //Period has actual hours.

  private completeSubject = new Subject<Period>();

  private readonly numberOfYears = 2;
  public maxPeriod = 52 * this.numberOfYears;
  public maxDate: DateTime = DateTime.now().plus({ years: this.numberOfYears });

  public constructor(
    private periodService: PeriodService,
    private logger: PortalLoggerService
  ) {
    super();
  }

  public async ngOnInit() {
    if (!this.sourcePeriod) {
      throw new Error('Source Period is undefined');
    }
    this.firstPlannablePeriod = await this.periodService.getCurrentPeriod();
    this.destinationPeriod = await this.periodService.getPeriod(this.sourcePeriod.id + 1);

    const targetPeriodId = this.sourcePeriod.id + this.maxPeriod;
    const maxPeriod = await this.periodService.getPeriod(targetPeriodId);

    this.setIsShiftInvalid();
    if (!maxPeriod) {
      this.logger.error(`Cant find period: ${targetPeriodId}`);
      return;
    }
    this.maxDate = maxPeriod.endDate;
  }

  public observePeriodSelection() {
    return this.completeSubject.asObservable();
  }

  public onSubmit() {
    if (!this.destinationPeriod) {
      return;
    }

    this.completeSubject.next(this.destinationPeriod);
  }

  public async changePeriodByCount(shiftPeriodCount: number) {
    if (shiftPeriodCount < 0) {
      this.destinationPeriod = this.sourcePeriod;
      this.shiftPeriodCount = 0;
      this.setIsShiftInvalid();
      return;
    }

    const targetPeriodId = this.sourcePeriod.id + shiftPeriodCount;
    const destinationPeriod = await this.periodService.getPeriod(targetPeriodId);
    if (!destinationPeriod) {
      this.logger.error(`Can't find period: ${targetPeriodId}`);
      return;
    }
    this.destinationPeriod = destinationPeriod;

    this.shiftPeriodCount = this.destinationPeriod.id - this.sourcePeriod.id;
    this.setIsShiftInvalid();
  }

  public setPeriod(period: Period | undefined) {
    if (period) {
      this.shiftPeriodCount = period.id - this.sourcePeriod.id;
    }
    this.destinationPeriod = period;
    this.setIsShiftInvalid();
  }

  private setIsShiftInvalid() {
    if (!this.destinationPeriod || !this.firstPlannablePeriod) {
      this.isShiftInvalid = true;
      return;
    }

    this.isShiftInvalid = this.destinationPeriod.id < this.firstPlannablePeriod.id;
  }
}
